import React from "react";
import { Box, Text, Flex, SimpleGrid } from "@chakra-ui/react";

// ============ Custom Chakra-Compatible Icons ============

const BarChartIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    stroke="#2AA198"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const FileTextIcon = () => (
  <svg
    width="26"
    height="26"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const GridIcon = () => (
  <svg
    width="26"
    height="26"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const MessageSquareIcon = () => (
  <svg
    width="26"
    height="26"
    fill="none"
    stroke="#2AA198"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// ============ Component ============

const ApproachSection = () => {
  const cards = [
    {
      icon: <BarChartIcon />,
      title: "Built for manufacturers",
      text: "Not adapted from analyst tools—designed specifically for manufacturing decision-makers."
    },
    {
      icon: <FileTextIcon />,
      title: "No internal data required",
      text: "Works entirely from public market data. Start immediately without IT integration."
    },
    {
      icon: <GridIcon />,
      title: "No dashboards to interpret",
      text: "Skip the complexity. Get conclusions and recommendations, not charts to decipher."
    },
    {
      icon: <MessageSquareIcon />,
      title: "Plain language explanations",
      text: "Insights written for business leaders, not data scientists. Understand everything at a glance."
    },
    {
      icon: <BarChartIcon />,
      title: "Guidance over raw signals",
      text: "We don’t just show data—we tell you what it means and what to consider doing.",
      center: true
    }
  ];

  return (
    <Box w="full" py="24" bg="#F9FBFD">
      <Box maxW="6xl" mx="auto" px="6" textAlign="center">

        {/* Top Heading */}
        <Text
          color="green.700"
          fontWeight="semibold"
          letterSpacing="widest"
          fontSize="sm"
        >
          WHY WE'RE DIFFERENT
        </Text>

        <Text
          fontSize="5xl"
          fontFamily="serif"
          fontWeight="bold"
          mt="4"
          color="gray.900"
        >
          A new <Text as="span" color="#2AA198">approach</Text> to market intelligence
        </Text>

        <Text
          mt="6"
          fontSize="lg"
          color="gray.600"
          maxW="3xl"
          mx="auto"
          lineHeight="tall"
        >
          Built from the ground up for how manufacturing leaders actually make decisions.
        </Text>

        {/* Cards Grid */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="8" mt="20">
          {cards.map((card, i) => (
            <Flex
              key={i}
              justify={card.center ? "center" : "flex-start"}
              gridColumn={card.center ? { md: "span 2" } : undefined}
            >
              <Box
                bg="white"
                p="8"
                rounded="3xl"
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
                w={card.center ? { base: "100%", md: "50%" } : "100%"}
                textAlign="left"
              >
                <Flex align="center" gap="4" mb="4">
                  <Flex bg="blue.50" p="4" rounded="xl">
                    {card.icon}
                  </Flex>

                  <Text
                    fontSize="2xl"
                    fontFamily="serif"
                    fontWeight="bold"
                    color="gray.900"
                  >
                    {card.title}
                  </Text>
                </Flex>

                <Text fontSize="lg" color="gray.600" lineHeight="tall">
                  {card.text}
                </Text>
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default ApproachSection;